import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Trash2, X, FileText } from 'lucide-react';
import { useAdrs, useAdrActions, useBoards, useEmployees } from '../stores';
import { fuzzyFilter, highlightMatches } from '../boardv3/utils/fuzzySearch';
import { blocksToText } from '../lib/blocksToText';
import { MultiSelectDropdown, type DropdownOption } from '../components/MultiSelectDropdown';
import { HighlightedText } from '../components/HighlightedText';
import { AdrModal } from './components/AdrModal';
import { PageContainer, PageHeader, PageEmptyState } from '../components/PageLayout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { relativeDate } from '../lib/dateUtils';
import { getScopeLabel } from '../lib/scopeLabel';
import { STATUS_COLORS, IMPL_STATUS_COLORS } from './adrColors';
import type { ADR, Employee, BoardDefinition } from '../types';

// ============================================
// AdrRow
// ============================================

function AdrRow({
  adr,
  employeeMap,
  boardMap,
  searchQuery,
  onDelete,
  onSelect,
}: {
  adr: ADR;
  employeeMap: Map<string, Employee>;
  boardMap: Map<string, BoardDefinition>;
  searchQuery: string;
  onDelete: (adrId: string) => void;
  onSelect: (adrId: string) => void;
}) {
  const { t } = useTranslation();
  const titleSegments = highlightMatches(searchQuery, adr.title || t('adr.newAdr'));

  return (
    <tr
      className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
      onClick={() => onSelect(adr.id)}
    >
      {/* Title */}
      <td className="px-3 py-1.5 text-xs text-gray-900 max-w-[280px]">
        <div className="truncate font-medium">
          <HighlightedText segments={titleSegments} />
        </div>
      </td>

      {/* Status */}
      <td className="px-3 py-1.5">
        <div className="flex items-center gap-1.5">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: STATUS_COLORS[adr.status] }}
          />
          <span className="text-xs text-gray-600">
            {t(`adr.status.${adr.status}` as any)}
          </span>
        </div>
      </td>

      {/* Implementation Status */}
      <td className="px-3 py-1.5">
        <span
          className="inline-flex text-[10px] px-1.5 py-0.5 rounded font-medium"
          style={{
            backgroundColor: IMPL_STATUS_COLORS[adr.implementationStatus] + '20',
            color: IMPL_STATUS_COLORS[adr.implementationStatus],
          }}
        >
          {t(`adr.implStatus.${adr.implementationStatus}` as any)}
        </span>
      </td>

      {/* Scope */}
      <td className="px-3 py-1.5 text-xs text-gray-500">
        {getScopeLabel(adr.scope, boardMap, { global: t('adr.scopeGlobal'), board: t('adr.scopeBoard'), canvas: t('adr.scopeCanvas') })}
      </td>

      {/* People */}
      <td className="px-3 py-1.5">
        <div className="flex flex-wrap gap-1">
          {adr.people.length === 0 && (
            <span className="text-xs text-gray-300">-</span>
          )}
          {adr.people.map((p, i) => {
            const emp = employeeMap.get(p.employeeId);
            return (
              <span
                key={`${p.employeeId}-${p.role}-${i}`}
                className="inline-flex items-center gap-1 text-xs text-gray-600"
                title={`${emp?.name ?? p.employeeId} (${t(`adr.person.${p.role}` as any)})`}
              >
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: emp?.color ?? '#999' }}
                />
                <span className="truncate max-w-[80px]">{emp?.name ?? p.employeeId}</span>
              </span>
            );
          })}
        </div>
      </td>

      {/* Updated */}
      <td className="px-3 py-1.5 text-xs text-gray-500 whitespace-nowrap">
        {relativeDate(adr.updatedAt)}
      </td>

      {/* Delete */}
      <td className="px-3 py-1.5 text-right">
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(adr.id); }}
          className="p-1 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
          title={t('common.delete')}
        >
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  );
}

// ============================================
// AdrsPage
// ============================================

export function AdrsPage() {
  useDocumentTitle('ADRs – qtplan');
  const { t } = useTranslation();
  const adrs = useAdrs();
  const actions = useAdrActions();
  const boards = useBoards();
  const employees = useEmployees();

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editAdrId, setEditAdrId] = useState<string | undefined>(undefined);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [filterImplStatuses, setFilterImplStatuses] = useState<string[]>([]);
  const [filterScopes, setFilterScopes] = useState<string[]>([]);

  // Lookup maps
  const employeeMap = useMemo(() => {
    const map = new Map<string, Employee>();
    for (const e of employees) map.set(e.id, e);
    return map;
  }, [employees]);

  const boardMap = useMemo(() => {
    const map = new Map<string, BoardDefinition>();
    for (const b of boards) map.set(b.id, b);
    return map;
  }, [boards]);

  // Dropdown options
  const statusOptions: DropdownOption[] = useMemo(() => [
    { value: 'draft', label: t('adr.status.draft'), color: STATUS_COLORS.draft },
    { value: 'proposed', label: t('adr.status.proposed'), color: STATUS_COLORS.proposed },
    { value: 'accepted', label: t('adr.status.accepted'), color: STATUS_COLORS.accepted },
    { value: 'deprecated', label: t('adr.status.deprecated'), color: STATUS_COLORS.deprecated },
    { value: 'superseded', label: t('adr.status.superseded'), color: STATUS_COLORS.superseded },
  ], [t]);

  const implStatusOptions: DropdownOption[] = useMemo(() => [
    { value: 'pending', label: t('adr.implStatus.pending'), color: IMPL_STATUS_COLORS.pending },
    { value: 'in_progress', label: t('adr.implStatus.in_progress'), color: IMPL_STATUS_COLORS.in_progress },
    { value: 'done', label: t('adr.implStatus.done'), color: IMPL_STATUS_COLORS.done },
    { value: 'abandoned', label: t('adr.implStatus.abandoned'), color: IMPL_STATUS_COLORS.abandoned },
  ], [t]);

  const scopeOptions: DropdownOption[] = useMemo(() => {
    const opts: DropdownOption[] = [
      { value: 'global', label: t('adr.scopeGlobal') },
    ];
    for (const b of boards) {
      opts.push({ value: `board:${b.id}`, label: b.name });
    }
    // Check if any ADR uses canvas scope
    const hasCanvas = adrs.some(a => a.scope.type === 'canvas');
    if (hasCanvas) {
      opts.push({ value: 'canvas', label: t('adr.scopeCanvas') });
    }
    return opts;
  }, [t, boards, adrs]);

  // Apply filters
  const filteredAdrs = useMemo(() => {
    let result = adrs;

    // Status filter
    if (filterStatuses.length > 0) {
      result = result.filter(a => filterStatuses.includes(a.status));
    }

    // Implementation status filter
    if (filterImplStatuses.length > 0) {
      result = result.filter(a => filterImplStatuses.includes(a.implementationStatus));
    }

    // Scope filter
    if (filterScopes.length > 0) {
      result = result.filter(a => {
        if (a.scope.type === 'global') return filterScopes.includes('global');
        if (a.scope.type === 'board') return filterScopes.includes(`board:${a.scope.boardId}`);
        if (a.scope.type === 'canvas') return filterScopes.includes('canvas') || filterScopes.includes(`board:${a.scope.boardId}`);
        return false;
      });
    }

    // Search: fuzzy on title + fulltext on content
    if (searchQuery.trim()) {
      const lower = searchQuery.trim().toLowerCase();
      // First filter by content match
      const contentMatches = new Set<string>();
      for (const a of result) {
        if (blocksToText(a.context).includes(lower)) contentMatches.add(a.id);
        if (blocksToText(a.decision).includes(lower)) contentMatches.add(a.id);
        if (blocksToText(a.consequences).includes(lower)) contentMatches.add(a.id);
      }
      // Fuzzy filter on title
      const fuzzyResults = fuzzyFilter(result, searchQuery, (a) => a.title || '');
      // Merge: fuzzy results + content-matched items not already in fuzzy results
      const fuzzyIds = new Set(fuzzyResults.map(a => a.id));
      const contentOnly = result.filter(a => contentMatches.has(a.id) && !fuzzyIds.has(a.id));
      result = [...fuzzyResults, ...contentOnly];
    }

    return result;
  }, [adrs, searchQuery, filterStatuses, filterImplStatuses, filterScopes]);

  // Handlers
  const handleOpenCreate = useCallback(() => {
    setEditAdrId(undefined);
    setModalOpen(true);
  }, []);

  const handleOpenEdit = useCallback((adrId: string) => {
    setEditAdrId(adrId);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setEditAdrId(undefined);
  }, []);

  const handleDelete = useCallback((adrId: string) => {
    if (!confirm(t('adr.deleteConfirm'))) return;
    actions.deleteAdr(adrId);
  }, [t, actions]);

  const hasActiveFilters =
    filterStatuses.length > 0 ||
    filterImplStatuses.length > 0 ||
    filterScopes.length > 0;

  const clearAllFilters = useCallback(() => {
    setSearchQuery('');
    setFilterStatuses([]);
    setFilterImplStatuses([]);
    setFilterScopes([]);
  }, []);

  return (
    <PageContainer wide>
      {/* Header */}
      <PageHeader
        title={t('nav.adrs')}
        subtitle={
          filteredAdrs.length !== adrs.length
            ? t('adr.overview.subtitleFiltered', { count: adrs.length, filtered: filteredAdrs.length })
            : t('adr.overview.subtitle', { count: adrs.length })
        }
        buttonLabel={t('adr.overview.addButton')}
        onButtonClick={handleOpenCreate}
      />

      {adrs.length === 0 && !hasActiveFilters ? (
        <PageEmptyState
          icon={<FileText size={20} />}
          title={t('adr.overview.noAdrs')}
          description={t('adr.overview.noAdrsDesc')}
        />
      ) : (
        <>
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('adr.overview.searchPlaceholder')}
                className="w-full pl-8 pr-7 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-gray-400 focus:border-gray-400 text-gray-900 bg-white"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <MultiSelectDropdown
              options={statusOptions}
              selected={filterStatuses}
              onChange={setFilterStatuses}
              placeholder={t('adr.status.label')}
            />
            <MultiSelectDropdown
              options={implStatusOptions}
              selected={filterImplStatuses}
              onChange={setFilterImplStatuses}
              placeholder={t('adr.implStatus.label')}
            />
            <MultiSelectDropdown
              options={scopeOptions}
              selected={filterScopes}
              onChange={setFilterScopes}
              placeholder={t('adr.overview.scope')}
            />
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
              >
                {t('common.resetFilters')}
              </button>
            )}
          </div>

          {/* Table */}
          <div className="bg-white border border-gray-200 rounded overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500">
                      {t('adr.title')}
                    </th>
                    <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500">
                      {t('adr.status.label')}
                    </th>
                    <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500">
                      {t('adr.implStatus.label')}
                    </th>
                    <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500">
                      {t('adr.overview.scope')}
                    </th>
                    <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500">
                      {t('adr.person.label')}
                    </th>
                    <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500">
                      {t('adr.overview.updated')}
                    </th>
                    <th className="px-3 py-1.5 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {filteredAdrs.map((adr) => (
                    <AdrRow
                      key={adr.id}
                      adr={adr}
                      employeeMap={employeeMap}
                      boardMap={boardMap}
                      searchQuery={searchQuery}
                      onDelete={handleDelete}
                      onSelect={handleOpenEdit}
                    />
                  ))}
                  {filteredAdrs.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-8 text-center text-xs text-gray-400">
                        {adrs.length === 0
                          ? t('adr.overview.noAdrs')
                          : t('adr.overview.noResults')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ADR Modal */}
      <AdrModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        adrId={editAdrId}
      />
    </PageContainer>
  );
}
